import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDoctorContext } from "../../../Navigation/context/DoctorContext";
import { useToast } from "../../../Common/hooks/useToast";
import { Consultation } from "../../../Common/interfaces/doctor.interfaces";
import {
  fetchActiveConsultation,
  fetchRecentHistory,
  toggleDoctorStatus,
  respondConsultation,
} from "../https/dashboard_module.https";
import { DashboardModuleHookReturn } from "../interfaces/dashboard_module.interface";

export const useDashboardModule = (): DashboardModuleHookReturn => {
  const navigate = useNavigate();
  const { clearIncomingRequest, doctorProfile, isDoctorProfileLoading } = useDoctorContext();
  const { showToast } = useToast();

  const [isOnline, setIsOnline] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeConsultation, setActiveConsultation] = useState<Consultation | null>(null);
  const [recentConsultations, setRecentConsultations] = useState<Consultation[]>([]);
  const [ongoingTimer, setOngoingTimer] = useState("00:00");
  const ongoingTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isDoctorProfileLoading) return;
    const doctorId = localStorage.getItem("doctorId");
    const doctorRole = localStorage.getItem("doctorRole");
    const isLoggedIn = !!doctorId && doctorRole === "DOCTOR";
    if (!isLoggedIn) {
      navigate("/");
      return;
    }
    setIsOnline(true);
    const loadData = async () => {
      await Promise.all([checkActiveConsultation(), loadRecentHistory()]);
      setIsLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoctorProfileLoading]);

  useEffect(() => {
    if (ongoingTimerRef.current) clearInterval(ongoingTimerRef.current);
    if (activeConsultation?.started_at) {
      const startedAt = activeConsultation.started_at;
      const tick = () => {
        const secs = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
        setOngoingTimer(
          `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`
        );
      };
      tick();
      ongoingTimerRef.current = setInterval(tick, 1000);
    } else {
      setOngoingTimer("00:00");
    }
    return () => { if (ongoingTimerRef.current) clearInterval(ongoingTimerRef.current); };
  }, [activeConsultation]);

  const checkActiveConsultation = async () => {
    try {
      const response = await fetchActiveConsultation();
      setActiveConsultation(response.data?.data ?? null);
    } catch {
      // silently ignore — no active consultation is a normal state
    }
  };

  const loadRecentHistory = async () => {
    try {
      const response = await fetchRecentHistory({ page: 1, limit: 5 });
      if (response.data?.data) setRecentConsultations(response.data.data);
    } catch {
      // silently ignore
    }
  };

  const handleToggleStatus = async () => {
    if (!doctorProfile) return;
    const newStatus = isOnline ? "offline" : "online";
    try {
      await toggleDoctorStatus(doctorProfile._id, newStatus);
      setIsOnline(!isOnline);
      showToast(`Status updated to ${newStatus}`, "success");
    } catch {
      showToast("Failed to update status", "error");
    }
  };

  const handleAcceptRequest = async (consultationId: string) => {
    try {
      const response = await respondConsultation(consultationId, "accepted");
      if (response.data) {
        showToast("Request Accepted!", "success");
        clearIncomingRequest();
        navigate(`/consultation/${consultationId}`);
      }
    } catch (error: any) {
      showToast(
        error.response?.data?.message || "Failed to accept. It might have been taken by another doctor.",
        "error"
      );
      clearIncomingRequest();
      checkActiveConsultation();
    }
  };

  const handleRejectRequest = async (consultationId: string) => {
    try {
      await respondConsultation(consultationId, "rejected");
      clearIncomingRequest();
      showToast("Request Rejected", "success");
    } catch {
      clearIncomingRequest();
      showToast("Request already expired", "error");
    }
  };

  return {
    isLoading,
    isOnline,
    activeConsultation,
    recentConsultations,
    ongoingTimer,
    handleToggleStatus,
    handleAcceptRequest,
    handleRejectRequest,
  };
};
